import { ChartDataPoint } from 'features/systemLens'

export const computeSingleScaleRanges = (
  allItems: Array<{ id: string; name?: string }>,
  sensorRanges: Record<string, { min: number; max: number; unit: string }>,
  currentCustomRanges?: Record<string, { min?: number; max?: number }>
): Record<string, { min?: number; max?: number }> => {
  // Build a combined range object that uses custom ranges if available, otherwise auto ranges
  const effectiveRanges: Record<
    string,
    { min: number; max: number; unit: string }
  > = {}
  allItems.forEach(({ id: itemId }) => {
    const customRange = currentCustomRanges?.[itemId]
    const autoRange = sensorRanges[itemId]

    if (
      customRange &&
      customRange.min !== undefined &&
      customRange.max !== undefined
    ) {
      effectiveRanges[itemId] = {
        min: customRange.min,
        max: customRange.max,
        unit: autoRange?.unit || '',
      }
    } else if (autoRange) {
      effectiveRanges[itemId] = autoRange
    }
  })

  // For single scale, just find the overall min/max from the effective ranges
  let overallMin = Infinity
  let overallMax = -Infinity

  allItems.forEach((item) => {
    const data = effectiveRanges[item.id]
    if (data && isFinite(data.min) && isFinite(data.max)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  if (isFinite(overallMin) && isFinite(overallMax)) {
    const newRanges: Record<string, { min?: number; max?: number }> = {}
    const formattedMin = Number(overallMin.toPrecision(4))
    const formattedMax = Number(overallMax.toPrecision(4))
    allItems.forEach(({ id: itemId }) => {
      newRanges[itemId] = { min: formattedMin, max: formattedMax }
    })
    newRanges['single-scale'] = { min: formattedMin, max: formattedMax }
    return newRanges
  }
  return {}
}

export const computeMultipleScaleRanges = (
  allItems: Array<{ id: string; name?: string }>,
  chartData: ChartDataPoint[],
  sensorRanges?: Record<string, { min: number; max: number; unit: string }>
): Record<string, { min?: number; max?: number }> => {
  const newRanges: Record<string, { min?: number; max?: number }> = {}

  // If sensorRanges are provided (which already have buffer), use them
  if (sensorRanges) {
    allItems.forEach(({ id: itemId }) => {
      const range = sensorRanges[itemId]
      if (range && isFinite(range.min) && isFinite(range.max)) {
        newRanges[itemId] = {
          min: Number(range.min.toPrecision(4)),
          max: Number(range.max.toPrecision(4)),
        }
      }
    })
  } else {
    // Otherwise recompute from chart data with buffer
    allItems.forEach(({ id: itemId }) => {
      let min = Infinity
      let max = -Infinity

      chartData.forEach((point) => {
        const value = point[itemId]
        if (typeof value === 'number' && !isNaN(value)) {
          min = Math.min(min, value)
          max = Math.max(max, value)
        }
      })

      if (isFinite(min) && isFinite(max)) {
        const buffer = (max - min) * 0.1
        newRanges[itemId] = {
          min: Number((min - buffer).toPrecision(4)),
          max: Number((max + buffer).toPrecision(4)),
        }
      }
    })
  }

  return newRanges
}

export function calculateOverallRangeWithBuffer(
  items: Array<{ id: string; name?: string }>,
  sensorRanges: Record<string, { min: number; max: number; unit: string }>
): { bufferedMin: number; bufferedMax: number } | null {
  if (items.length === 0) {
    return null
  }

  let overallMin = Infinity
  let overallMax = -Infinity
  items.forEach((item) => {
    const data = sensorRanges[item.id]
    if (data && isFinite(data.min) && isFinite(data.max)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  if (isFinite(overallMin) && isFinite(overallMax)) {
    const buffer = (overallMax - overallMin) * 0.1
    return {
      bufferedMin: overallMin - buffer,
      bufferedMax: overallMax + buffer,
    }
  }

  return null
}
